import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

export interface NormalizedError {
  message: string;     // human-friendly description
  reason: string;      // short reason/cause
  status?: number;     // HTTP status when available
  statusText?: string; // HTTP status text when available
  raw?: unknown;       // original parsed payload (for logging)
}

@Injectable({ providedIn: 'root' })
export class ErrorNormalizerService {
  // Candidate paths hoisted to avoid re-allocations each call.
  private readonly msgPaths: readonly string[] = [
    // nested envelopes
    'error.message', 'error.Message',
    // direct keys
    'Message', 'message',
    // RFC 7807 ProblemDetails
    'detail',
    // common auth/REST fields
    'error_description',
    // sometimes server puts plain text here
    'error'
  ] as const;

  private readonly reasonPaths: readonly string[] = [
    // nested envelopes
    'error.reason', 'error.Reason',
    // direct keys
    'Reason', 'reason',
    // RFC 7807 fallback
    'title',
    // last resort: a type URL can be suggestive
    'type'
  ] as const;

  /**
   * Normalize any thrown error into { message, reason, status?, statusText?, raw? }.
   */
  extract(error: unknown): NormalizedError {
    let message = 'Error';
    let reason  = 'Unknown error';
    let status: number | undefined;
    let statusText: string | undefined;
    let raw: unknown = error;

    if (error instanceof HttpErrorResponse) {
      status = error.status;
      statusText = error.statusText;

      const payload = this.coercePayload(error.error ?? error.message);
      raw = payload;

      const messageValue = this.pick(payload, this.msgPaths);
      const reasonValue = this.pick(payload, this.reasonPaths);

      message = this.toDisplayString(messageValue) ?? statusText ?? 'Error fetching data';
      reason  = this.toDisplayString(reasonValue) ?? (status ? `HTTP ${status}` : 'Unknown error');

      message = this.summarizeMessage(message);
      reason  = this.summarizeReason(reason);

      // Special case: network error (status 0)
      if (!messageValue && status === 0) {
        message = 'Network error';
        reason  = 'The request did not reach the server';
      }
      }
      else if (error && typeof error === 'object') {
        const payload = this.coercePayload(error);
        raw = payload;

        const messageValue = this.getPathCI(payload, 'Message') ?? this.getPathCI(payload, 'message');
        const reasonValue = this.getPathCI(payload, 'Reason')  ?? this.getPathCI(payload, 'reason');

        if (messageValue != null) {
          message = this.summarizeMessage(this.toDisplayString(messageValue) ?? 'Error');
        }
        if (reasonValue != null) {
          reason  = this.summarizeReason(this.toDisplayString(reasonValue) ?? 'Unknown error');
        }
      }
      else if (typeof error === 'string') {
        message = this.summarizeMessage(error);
      }

      return { message, reason, status, statusText, raw };
  }

  // ---------- helpers ----------

  /** If value is a JSON string, parse; if plain string, wrap as { message }; else return as-is. */
  private coercePayload(raw: unknown): unknown {
    if (raw == null) {
      return undefined;
    }

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      const looksJson = (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
                        (trimmed.startsWith('[') && trimmed.endsWith(']'));
      if (looksJson) {
        try {
          return JSON.parse(trimmed);
        }
        catch {
          /* fall through */
        }
      }
      return { message: raw }; // keep original string
    }
    return raw;
  }

  /** Case-insensitive key search in a flat object. */
  private getKeyCI(obj: unknown, key: string): string | null {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return null;
    }

    const rec = obj as Record<string, unknown>;
    const found = Object.keys(rec).find(k => k.toLowerCase() === key.toLowerCase());
    return found ?? null;
  }

  /** Get nested path in a case-insensitive manner: e.g. "error.Message" */
  private getPathCI(obj: unknown, path: string): unknown {
    if (obj == null) {
      return undefined;
    }

    const parts = path.split('.');
    let cur: unknown = obj;

    for (const pathPart of parts) {
      if (cur == null || typeof cur !== 'object' || Array.isArray(cur)) {
        return undefined;
      }

      const real = this.getKeyCI(cur, pathPart);
      if (!real) {
        return undefined;
      }

      cur = (cur as Record<string, unknown>)[real];
    }
    return cur;
  }

  /** Try a list of candidate paths and return the first non-empty value. */
  private pick(obj: unknown, paths: readonly string[]): unknown {
    for (const path of paths) {
      const value = this.getPathCI(obj, path);
      if (value != null && value !== '') {
        return value;
      }
    }
    return undefined;
  }

  /** Convert unknown to string (JSON if object), or undefined if empty. */
  private toDisplayString(value: unknown): string | undefined {
    if (value == null) {
      return undefined;
    }

    if (typeof value === 'string') {
      return value;
    }

    try {
      // Avoid super noisy output for functions/symbols, etc.
      return JSON.stringify(value);
    }
    catch {
      return String(value);
    }
  }

  /** Make long backend messages readable: keep first line / trim noise. */
  private summarizeMessage(text: string): string {
    if (!text) {
      return 'Error';
    }

    // Normalize newlines
    let normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    // Prefer the first non-empty line
    const firstLine = normalizedText.split('\n').find(line => line.trim().length > 0) ?? normalizedText;
    // Cut noisy inner-exception preamble if present
    const clipped = firstLine.split(' ---')[0] ?? firstLine;
    return this.cleanWhitespace(clipped);
  }

  /** Shorten/normalize the "reason" field. */
  private summarizeReason(text: string): string {
    if (!text) {
      return 'Unknown error';
    }

    const normalizedText = text.replace(/\r\n/g, ' ').replace(/\s+/g, ' ').trim();
    return normalizedText || 'Unknown error';
  }

  private cleanWhitespace(string: string): string {
    return string.replace(/\s+/g, ' ').trim();
  }
}
